import React from "react"
import {
	Flex,
	Text,
	ContactCard,
	Button,
	IconTypes,
	Switch,
	Loader,
} from "@capioweb/ui-components/src"
import { AppContext, LocalesContext } from "../../../context"
import { UnitFilterContext } from "../context/UnitFilterContext"
import { UnitFilterProps, UnitItem } from "../"
import { DynamicLink } from "@capioweb/ui-components/src"
import { sendUnitListingClickDataLayerPush } from "../../../utils"
import { useQuery } from "react-query"
import { queryStaleTime } from "../../../constants"
import useSSR from "use-ssr"
import { getAllUnits } from "../../../api/getAllItems"
import { useSearchParams } from "react-router-dom"

const Unit: React.FC<UnitItem> = (props) => {
	return (
		<ContactCard icon={props.icon ? (props.icon as IconTypes) : null}>
			<Text variant="headline-4" mb={1} mr={2}>
				{props.displayName}
			</Text>
			<Text fontSize={1.75} mb={0}>
				{props.address.split(",")[0]}
			</Text>
			{props.address.indexOf(",") !== -1 && (
				<Text fontSize={1.75} mb={0}>
					{props.address.substring(
						props.address.indexOf(",") + 1,
						props.address.length
					)}
				</Text>
			)}
		</ContactCard>
	)
}

const UnitList: React.FC<UnitFilterProps> = (props) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { activeRegionIds, activeCareTypeIds, activeDiagnoseFirstLevelIds, activeDiagnoseSecondLevelIds, setActiveDiagnoseFirstLevelFilterValues, setActiveDiagnoseSecondLevelFilterValues, setTotalHits } =
		React.useContext(UnitFilterContext)
	const { getUserLocation, userLocation } = React.useContext(AppContext)

	const [activeUnits, setActiveUnits] = React.useState(props.items)
	const [showAll, setShowAll] = React.useState(false)
	const [showGeoPosition, setShowGeoPosition] = React.useState(false)

	const prevFilterValues = React.useRef({
		activeRegionIds,
		activeCareTypeIds,
		activeDiagnoseFirstLevelIds,
		activeDiagnoseSecondLevelIds,
	})
	const initialUnitLimit = 12

	const { isBrowser } = useSSR()

	const handleGeoPositionSwitch = (checked: boolean) => {
		setShowGeoPosition(checked)
		setShowAll(true)
		getUserLocation()

	}

	// Reset unit list if we don't have any active filters
	const resetUnitList = () => {
		// If showAll we want to show all units if avaliable. Fallback to initial units from props
		setActiveUnits(showAll ? data?.items ?? props.items : props.items)
		prevFilterValues.current = { activeRegionIds, activeCareTypeIds, activeDiagnoseFirstLevelIds, activeDiagnoseSecondLevelIds }
	}
	const { data, isLoading } = useQuery(
		["units", props.careTypeIds, props.diagnoseIds, userLocation, showGeoPosition, activeRegionIds, activeCareTypeIds, activeDiagnoseFirstLevelIds, activeDiagnoseSecondLevelIds],
		() =>
			isBrowser &&
			getAllUnits(
				props.careTypeIds.join(","),
				props.diagnoseIds.join(","),
				activeDiagnoseFirstLevelIds.join(","),
				activeDiagnoseSecondLevelIds.join(","),
				activeCareTypeIds.join(","),
				activeRegionIds.join(","),
				props.blockId,
				showGeoPosition ? userLocation?.coords?.longitude.toString() ?? "" : "",
				showGeoPosition ? userLocation?.coords?.latitude.toString() ?? "" : ""
			),
		{
			staleTime: queryStaleTime,
		}
	)

	const ActivateFilter = () => {
		const hasNoActiveFilters =
			activeRegionIds.length === 0 && activeCareTypeIds.length === 0 && activeDiagnoseFirstLevelIds.length === 0 && activeDiagnoseSecondLevelIds.length === 0
				? true
				: false

		// Reset unit list if we don't have any active filters
		if (hasNoActiveFilters) {
			resetUnitList()
			return
		}

		// Filter by active region if activeRegionIds has changed
		if (
			prevFilterValues.current.activeRegionIds !== activeRegionIds ||
			prevFilterValues.current.activeCareTypeIds !== activeCareTypeIds ||
			prevFilterValues.current.activeDiagnoseFirstLevelIds !== activeDiagnoseFirstLevelIds ||
			prevFilterValues.current.activeDiagnoseSecondLevelIds !== activeDiagnoseSecondLevelIds
		) {
			setShowAll(true)
			setActiveUnits(data?.items ?? props.items)
		}
	}

	React.useEffect(() => {
		ActivateFilter()
		setActiveDiagnoseSecondLevelFilterValues(data?.diagnoseSecondLevelFilterValues)
	}, [activeRegionIds, activeCareTypeIds, activeDiagnoseFirstLevelIds, activeDiagnoseSecondLevelIds])

	React.useEffect(() => {
		if (data?.items && showAll) {
			setActiveUnits(data?.items)
			ActivateFilter()
			setActiveDiagnoseFirstLevelFilterValues(data.diagnoseFirstLevelFilterValues)
			setActiveDiagnoseSecondLevelFilterValues(data.diagnoseSecondLevelFilterValues)
		}
		isLoading ? setTotalHits(0) : setTotalHits(data?.items?.length ?? props.totalItems)
	}, [data])

	return (<>
		<Flex display={{ _: 'flex', m: 'grid' }} w={1} flexDirection='column' justifyContent="center" style={{ gridTemplateColumns: '1fr 1fr 1fr' }} >
			{!isLoading &&
				<Flex style={{ gridColumn: 2 }} mx='auto' alignItems='center'>
					<Text textAlign="center" mb={2}>
						{translate(translationKeys.unitFilterResultCount).replace(
							/%s/g,
							!showAll
								? data?.totalItems.toString() ?? props.totalItems.toString()
								: activeUnits.length.toString()
						)}
						:
					</Text>
				</Flex>
			}
			<Flex mb={2} style={{ gridColumn: 3 }} mx='auto' mr={{ m: 0 }}>
				<Switch
					onChange={(e) => handleGeoPositionSwitch(e.target.checked)}
					label={translate(translationKeys.useMyPosition)}
					disabled={isLoading}
				/>
			</Flex>
		</Flex>
		<>
			{!isLoading ? (
				<>
					<Flex w={1} columnGap={{ m: 3 }} rowGap={{ _: 2, m: 3 }} flexWrap="wrap">
						{activeUnits.map((unit, i) => {
							return (
								<Flex
									w={1}
									flexGrow={0}
									flexShrink={1}
									flexBasis={{
										_: "100%",
										m: "calc(100% / 2 - 12px)",
										l: "calc(100% / 3 - 16px)",
									}}
									key={i}
								>
									<DynamicLink
										onClick={() =>
											sendUnitListingClickDataLayerPush(
												unit.displayName ?? "",
												props.careTypesFilterValues[0]?.name ?? ""
											)
										}
										url={unit?.url ?? "#"}
										style={{ width: "100%", height: "100%" }}
										target={unit?.target}
									>
										<Unit {...unit} />
									</DynamicLink>
								</Flex>
							)
						})}
					</Flex>
				</>
			) : (
				<Flex my={20}>
					<Loader />
				</Flex>
			)
			}
		</>
		{
			!showAll &&
			props.totalItems > initialUnitLimit &&
			activeUnits.length >= initialUnitLimit && (
				<Button.Link
					mt={{ _: 2, m: 3 }}
					variant="ghost"
					icon="plus"
					textColor="navy"
					disabled={isLoading}
					to="?next=true"
					onClick={(e) => {
						e.preventDefault()
						setShowAll(true)
						setActiveUnits(data?.items ?? props.items)
					}}
				>
					{isLoading
						? translate(translationKeys.loadUnits)
						: translate(translationKeys.showMore)}
				</Button.Link>
			)
		}
	</>
	)
}

export { UnitList }
